import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { subDays, startOfDay, endOfDay, format, parseISO, differenceInDays, startOfWeek, eachDayOfInterval } from 'date-fns';
import listOrdersApi from '../../order/api/list.api.order';
import MetricCard from '../../components/MetricCard/MetricCard';
import DateRangeFilter from '../../components/DateRangeFilter/DateRangeFilter';
import StatisticsChart from '../../components/StatisticsChart/StatisticsChart';
import formatCurrency from '../../lib/formatCurrency';
import {
  Container,
  Header,
  Title,
  MetricsGrid,
  ChartsGrid,
  Section,
  SectionTitle,
  DepartmentGrid,
  DepartmentCard,
  DepartmentName,
  DepartmentRevenue,
  EmptyState,
} from './StatisticsContainer.styled';

const StatisticsContainer = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 29)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const query = {
        limit: 1000000,
        offset: 0,
        populate: 'orderRows,department,createdBy',
        createdAt__gte: startDate.toISOString(),
        createdAt__lte: endDate.toISOString(),
        status__ne: -3, // Exclude cancelled orders
      };
      
      const results = await listOrdersApi(query);
      setOrders(results || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Calculate metrics
  const calculateMetrics = () => {
    const totalRevenue = orders.reduce((sum, order) => {
      const orderTotal = order.orderRows?.reduce((rowSum, row) => {
        return rowSum + (parseFloat(row.price) || 0) * (parseInt(row.quantity) || 0);
      }, 0) || 0;
      return sum + orderTotal;
    }, 0);

    const totalOrders = orders.length;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    return {
      totalRevenue: totalRevenue.toFixed(2),
      totalOrders,
      averageOrderValue: averageOrderValue.toFixed(2),
    };
  };

  // Revenue trend by date
  const getRevenueTrend = () => {
    const daysDiff = differenceInDays(endDate, startDate);
    const useWeeklyAggregation = daysDiff > 60;
    
    const revenueByDate = {};
    
    orders.forEach((order) => {
      const orderDate = parseISO(order.createdAt);
      // For longer ranges, aggregate by week start; otherwise use daily
      const dateKey = useWeeklyAggregation
        ? format(startOfWeek(orderDate, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        : format(orderDate, 'yyyy-MM-dd');
      
      const orderTotal = order.orderRows?.reduce((sum, row) => {
        return sum + (parseFloat(row.price) || 0) * (parseInt(row.quantity) || 0);
      }, 0) || 0;

      if (!revenueByDate[dateKey]) {
        revenueByDate[dateKey] = 0;
      }
      revenueByDate[dateKey] += orderTotal;
    });

    // Generate all dates/weeks in range to show gaps
    const allDates = eachDayOfInterval({ start: startDate, end: endDate });
    const dateKeys = new Set();
    
    allDates.forEach((date) => {
      const key = useWeeklyAggregation
        ? format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        : format(date, 'yyyy-MM-dd');
      dateKeys.add(key);
    });

    return Array.from(dateKeys)
      .sort()
      .map((dateKey) => ({
        name: useWeeklyAggregation
          ? `${t('weekShort')}${format(parseISO(dateKey), 'w')}`  // Week number
          : format(parseISO(dateKey), 'MMM dd'),
        value: parseFloat((revenueByDate[dateKey] || 0).toFixed(2)),
      }));
  };

  // Top products
  const getTopProducts = () => {
    const productCounts = {};

    orders.forEach((order) => {
      order.orderRows?.forEach((row) => {
        const productName = row.name || 'Unknown';
        const quantity = parseInt(row.quantity) || 0;
        
        if (!productCounts[productName]) {
          productCounts[productName] = 0;
        }
        productCounts[productName] += quantity;
      });
    });

    return Object.entries(productCounts)
      .map(([name, count]) => ({
        name: name.length > 25 ? name.substring(0, 25) + '...' : name,
        value: count,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  };

  // Brand distribution
  const getBrandDistribution = () => {
    const brandCounts = {};

    orders.forEach((order) => {
      order.orderRows?.forEach((row) => {
        // Try to extract brand from product name (assuming brand is at the start)
        const productName = row.name || '';
        const brandMatch = productName.split(' ')[0];
        const brand = brandMatch || 'Other';
        
        if (!brandCounts[brand]) {
          brandCounts[brand] = 0;
        }
        brandCounts[brand] += parseInt(row.quantity) || 0;
      });
    });

    return Object.entries(brandCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  // Department performance
  const getDepartmentPerformance = () => {
    const departmentRevenue = {};

    orders.forEach((order) => {
      const deptName = order.department?.name || 'Unknown Department';
      const orderTotal = order.orderRows?.reduce((sum, row) => {
        return sum + (parseFloat(row.price) || 0) * (parseInt(row.quantity) || 0);
      }, 0) || 0;

      if (!departmentRevenue[deptName]) {
        departmentRevenue[deptName] = 0;
      }
      departmentRevenue[deptName] += orderTotal;
    });

    return Object.entries(departmentRevenue)
      .map(([name, revenue]) => ({
        name,
        revenue: parseFloat(revenue.toFixed(2)),
      }))
      .sort((a, b) => b.revenue - a.revenue);
  };

  const metrics = calculateMetrics();
  const revenueTrend = getRevenueTrend();
  const topProducts = getTopProducts();
  const brandDistribution = getBrandDistribution();
  const departmentPerformance = getDepartmentPerformance();

  if (loading && orders.length === 0) {
    return (
      <Container>
        <Header>
          <Title>{t('statistics')}</Title>
        </Header>
        <EmptyState>
          <i className="mdi mdi-loading mdi-spin" />
          {t('loadingStatistics')}
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('statistics')}</Title>
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateRangeChange}
        />
      </Header>

      <MetricsGrid>
        <MetricCard
          icon="mdi-currency-eur"
          label={t('totalRevenue')}
          value={formatCurrency(metrics.totalRevenue)}
          loading={loading}
        />
        <MetricCard
          icon="mdi-chart-line"
          label={t('averageOrderValue')}
          value={formatCurrency(metrics.averageOrderValue)}
          loading={loading}
        />
        <MetricCard
          icon="mdi-cart-outline"
          label={t('totalOrdersLabel')}
          value={metrics.totalOrders}
          loading={loading}
        />
      </MetricsGrid>

      <ChartsGrid $fullWidth={differenceInDays(endDate, startDate) > 60}>
        <StatisticsChart
          type="line"
          data={revenueTrend}
          title={t('revenueTrend')}
          xKey="name"
          yKey="value"
          loading={loading}
        />
      </ChartsGrid>

      <Section>
        <SectionTitle>{t('productAnalytics')}</SectionTitle>
        <ChartsGrid>
          <StatisticsChart
            type="bar"
            data={topProducts}
            title={t('topProducts')}
            xKey="name"
            yKey="value"
            loading={loading}
            multicolor={true}
          />
          <StatisticsChart
            type="pie"
            data={brandDistribution}
            title={t('brandDistribution')}
            dataKey="value"
            loading={loading}
          />
        </ChartsGrid>
      </Section>

      <Section>
        <SectionTitle>{t('departmentPerformance')}</SectionTitle>
        {departmentPerformance.length > 0 ? (
          <DepartmentGrid>
            {departmentPerformance.map((dept, index) => (
              <DepartmentCard key={index}>
                <DepartmentName>
                  <i className="mdi mdi-domain" />
                  {dept.name}
                </DepartmentName>
                <DepartmentRevenue>{formatCurrency(dept.revenue)}</DepartmentRevenue>
              </DepartmentCard>
            ))}
          </DepartmentGrid>
        ) : (
          <EmptyState>{t('noDepartmentData')}</EmptyState>
        )}
      </Section>
    </Container>
  );
};

export default StatisticsContainer;
